import moment from 'moment'
import store from '@/store'
import {
  CalendarCheck,
  CalendarX,
  Users,
  Tag,
  Handshake,
  XCircle,
  User,
  UserX,
  BanknoteArrowUp,
  Building,
  MessageSquare,
  Mail,
  Phone,
  Flag
} from 'lucide-vue'

export class Condition {
  static Type = {
    Location: 'location',
    AccountManager: 'account_manager',
    AccountStatus: 'account_status',
    Overdue: 'overdue',
    DueDate: 'due_date',
    Customers: 'customers',
    LastName: 'last_name',
    AccountTags: 'account_tags',
    UnConfirmedPromises: 'unconfirmed',
    ConfirmedPromises: 'confirmed',
    Autopay: 'autopay',
    ScheduledPayment: 'scheduled_payment',
    RegisteredCustomer: 'registered',
    UnregisteredCustomer: 'unregistered',
    CollectorAction: 'collector_action',
    ActionDateRange: 'action_date_range',
    TextEnabled: 'text_enabled',
    EmailEnabled: 'email_enabled',
    AiCallsEnabled: 'ai_calls_enabled'
  }

  static currentID = 0

  constructor(type) {
    this.id = ++Condition.currentID
    this.type = type
  }
}

export class LocationCondition extends Condition {
  icon = Building

  constructor(locations) {
    super(Condition.Type.Location)
    this.locations = locations
  }

  get filter() {
    return { locations: this.locations.map(item => item.id).join(',') }
  }

  get short() {
    return this.locations.length
  }

  get text() {
    let text = `${this.locations.length} Location`
    if (this.locations.length > 1) text += 's'

    return text
  }
}

export class AccountManagerCondition extends Condition {
  icon = User

  constructor(accountManagers) {
    super(Condition.Type.AccountManager)
    this.accountManagers = accountManagers
  }

  get filter() {
    return {
      account_managers: this.accountManagers.map(item => item.pkid).join(',')
    }
  }

  get short() {
    return this.accountManagers.length
  }

  get text() {
    let text = `${this.accountManagers.length} Account Manager`
    if (this.accountManagers.length > 1) text += 's'

    return text
  }
}

export class OverdueCondition extends Condition {
  icon = CalendarX

  constructor(min, max) {
    super(Condition.Type.Overdue)
    this.min = min
    this.max = max
  }

  get filter() {
    const result = {}
    if (this.min !== null && this.min !== undefined)
      result.overdue_min = this.min
    if (this.max !== null && this.max !== undefined)
      result.overdue_max = this.max

    return result
  }

  get short() {
    if (this.min && this.max) {
      return `${this.min} - ${this.max}`
    } else if (this.min) {
      return this.min === 1 ? null : `≥ ${this.min}`
    } else {
      return `≤ ${this.max}`
    }
  }

  get text() {
    if (this.min && this.max) {
      return `${this.min} - ${this.max} Days Overdue`
    } else if (this.min) {
      return this.min === 1 ? 'Overdue' : `≥ ${this.min} Days Overdue`
    } else {
      return `≤ ${this.max} Days Overdue`
    }
  }
}

export class LastNameCondition extends Condition {
  icon = Users

  constructor(start, end) {
    super(Condition.Type.LastName)
    this.start = start?.toUpperCase() || end?.toUpperCase()
    this.end = end?.toUpperCase() || this.start
  }

  get filter() {
    return { last_name: [this.start, this.end].join(',') }
  }

  get short() {
    return this.start === this.end ? this.start : `${this.start} - ${this.end}`
  }

  get text() {
    if (this.start !== this.end) {
      return `Last Names ${this.start} - ${this.end}`
    } else {
      return `Last Name Starts with ${this.start}`
    }
  }
}

export class CustomersCondition extends Condition {
  icon = Users

  constructor(customers) {
    super(Condition.Type.Customers)
    this.customers = customers
  }

  get text() {
    const n = this.customers.length
    if (n === 1) return `1 ${store.getters.customerLabel}`

    return `${n} ${store.getters.customersLabel}`
  }
}

export class AccountTagsCondition extends Condition {
  icon = Tag

  constructor(slugs) {
    super(Condition.Type.AccountTags)
    this.slugs = slugs
  }

  get filter() {
    return { account_tags: this.slugs.join(',') }
  }

  get short() {
    return this.slugs.length
  }

  get text() {
    return `Account Tags: ${this.slugs.join(', ')}`
  }
}

export class AccountStatusCondition extends Condition {
  icon = Flag

  // Selectable account statuses. INACTIVE is included, but the customer list
  // only returns inactive accounts when INACTIVE is explicitly selected here;
  // every other query/filter excludes them server-side.
  static Labels = {
    ACTIVE: 'Active',
    BANKRUPT: 'Bankrupt',
    DO_NOT_CONTACT: 'Do Not Contact',
    NO_COMMUNICATION: 'No Communication',
    VOIDED: 'Voided',
    REPO: 'Repo',
    INACTIVE: 'Inactive'
  }

  constructor(statuses) {
    super(Condition.Type.AccountStatus)
    this.statuses = statuses
  }

  get filter() {
    return { account_status: this.statuses.join(',') }
  }

  get short() {
    return this.statuses.length
  }

  get text() {
    const labels = this.statuses.map(
      status => AccountStatusCondition.Labels[status] || status
    )

    return `Account Status: ${labels.join(', ')}`
  }
}

export class ConfirmedPromiseCondition extends Condition {
  icon = Handshake

  constructor(value = true) {
    super(Condition.Type.ConfirmedPromises)
    this.value = value
  }

  get filter() {
    return { confirmed: this.value }
  }

  get text() {
    return this.value
      ? 'Has Confirmed Promise'
      : 'Does Not Have Confirmed Promise'
  }
}

export class UnConfirmedPromiseCondition extends Condition {
  icon = XCircle

  constructor(value = true) {
    super(Condition.Type.UnConfirmedPromises)
    this.value = value
  }

  get filter() {
    return { unconfirmed: this.value }
  }

  get text() {
    return this.value
      ? 'Has Unconfirmed Promise'
      : 'Does Not Have Unconfirmed Promise'
  }
}

export class AutopayCondition extends Condition {
  icon = BanknoteArrowUp

  constructor(value = true) {
    super(Condition.Type.Autopay)
    this.value = value
  }

  get filter() {
    return { autopay: this.value }
  }

  get text() {
    return this.value ? 'Has Autopay' : 'Does Not Have Autopay'
  }
}

export class TextEnabledCondition extends Condition {
  icon = MessageSquare

  constructor(value = true) {
    super(Condition.Type.TextEnabled)
    this.value = value
  }

  get filter() {
    return { text_enabled: this.value }
  }

  get text() {
    return this.value ? 'Has Text Enabled' : 'Does Not Have Text Enabled'
  }
}

export class EmailEnabledCondition extends Condition {
  icon = Mail

  constructor(value = true) {
    super(Condition.Type.EmailEnabled)
    this.value = value
  }

  get filter() {
    return { email_enabled: this.value }
  }

  get text() {
    return this.value ? 'Has Email Enabled' : 'Does Not Have Email Enabled'
  }
}

export class AiCallsEnabledCondition extends Condition {
  icon = Phone

  constructor(value = true) {
    super(Condition.Type.AiCallsEnabled)
    this.value = value
  }

  get filter() {
    return { ai_calls_enabled: this.value }
  }

  get text() {
    return this.value
      ? 'Has AI Calls Enabled'
      : 'Does Not Have AI Calls Enabled'
  }
}

export class ScheduledPaymentCondition extends Condition {
  icon = CalendarCheck

  constructor(value = true) {
    super(Condition.Type.ScheduledPayment)
    this.value = value
  }

  get filter() {
    return { scheduled_payment: this.value }
  }

  get text() {
    return this.value
      ? 'Has Scheduled Payment'
      : 'Does Not Have Scheduled Payment'
  }
}

export class RegisteredCustomerCondition extends Condition {
  icon = User

  constructor() {
    super(Condition.Type.RegisteredCustomer)
  }

  get filter() {
    return { registered: true }
  }

  get text() {
    return `Registered ${store.getters.customersLabel}`
  }
}

export class UnregisteredCustomerCondition extends Condition {
  icon = UserX

  constructor() {
    super(Condition.Type.UnregisteredCustomer)
  }

  get filter() {
    return { registered: false }
  }

  get text() {
    return `Unregistered ${store.getters.customersLabel}`
  }
}

// New conditions for collector action filtering from Collection Stats
export class CollectorActionCondition extends Condition {
  constructor(actionTypes, userId) {
    super('collector_action')
    this.actionTypes = actionTypes
    this.userId = userId
  }

  get filter() {
    const result = {}
    if (this.actionTypes) {
      result.action_types = this.actionTypes
    }
    if (this.userId) {
      result.action_user_id = this.userId
    }

    return result
  }

  get short() {
    return this.text
  }

  get text() {
    if (this.actionTypes) {
      const actionMap = {
        AUTOPAY_CREATED: 'Has Autopay',
        PROMISE_CREATED: 'Has Promise',
        PROMISE_CONFIRMED: 'Promise Confirmed',
        MESSAGE_SENT: 'Collector Text',
        AUTOMATED_MESSAGE_SENT: 'Auto Text',
        GROUP_MESSAGE_SENT: 'Group Message',
        AI_CALL_MADE: 'AI Call',
        NOTE_CREATED: 'Has Notes',
        MARKED_AS_WORKED_TODAY: 'Has Worked',
        PAYMENT_COMPLETED: 'Has Payments',
        NO_CONTACT: 'No Contact'
      }

      return actionMap[this.actionTypes] || this.actionTypes
    }

    return 'Collector Activity'
  }
}

export class ActionDateRangeCondition extends Condition {
  constructor(dateFrom, dateTo) {
    super('action_date_range')
    this.dateFrom = dateFrom
    this.dateTo = dateTo
  }

  get filter() {
    const result = {}
    if (this.dateFrom) {
      result.action_date_from = this.dateFrom
    }
    if (this.dateTo) {
      result.action_date_to = this.dateTo
    }

    return result
  }

  get short() {
    const from = this.dateFrom ? moment(this.dateFrom) : null
    const to = this.dateTo ? moment(this.dateTo) : null

    if (from && to) {
      if (from.isSame(to, 'day')) {
        return from.format('MMM D')
      }

      return `${from.format('MMM D')} - ${to.format('MMM D')}`
    }
    if (from) return `From ${from.format('MMM D')}`
    if (to) return `Until ${to.format('MMM D')}`

    return 'Date Range'
  }

  get text() {
    return 'Date Range'
  }
}

export class DueDateCondition extends Condition {
  icon = CalendarCheck

  constructor(min, max) {
    super(Condition.Type.DueDate)
    this.min = min
    this.max = max
  }

  get filter() {
    const result = {}
    if (this.min !== null && this.max !== null) {
      // Both min and max provided - use range format
      result.due_date_range = `${this.min},${this.max}`
    } else if (this.min !== null) {
      // Only min provided - use single value
      result.due_date_range = `${this.min}`
    } else if (this.max !== null) {
      // Only max provided - use single value
      result.due_date_range = `${this.max}`
    }

    return result
  }

  get short() {
    if (this.min !== null && this.max !== null) {
      return `${this.min} - ${this.max}`
    } else if (this.min !== null) {
      return `≥ ${this.min}`
    } else if (this.max !== null) {
      return `≤ ${this.max}`
    }

    return ''
  }

  get text() {
    if (this.min !== null && this.max !== null) {
      if (this.min === this.max) {
        return this.min === 0
          ? 'Due Today'
          : `Due in ${this.min} Day${this.min === 1 ? '' : 's'}`
      }

      return `Due in ${this.min} - ${this.max} Days`
    } else if (this.min !== null) {
      return this.min === 0 ? 'Due Today or Later' : `Due in ${this.min}+ Days`
    } else if (this.max !== null) {
      return this.max === 0
        ? 'Due Today or Earlier'
        : `Due in ${this.max} Days or Less`
    }

    return 'Days Until Due Date'
  }
}

export function parseFilter(filter) {
  if (!filter) return []

  const conditions = []
  if (filter?.overdue_min || filter?.overdue_max) {
    conditions.push(
      new OverdueCondition(filter?.overdue_min, filter?.overdue_max)
    )
  }

  if (filter?.due_date_range) {
    const range = filter.due_date_range.split(',')
    const min = range[0] ? parseInt(range[0]) : null
    const max = range[1] ? parseInt(range[1]) : null
    conditions.push(new DueDateCondition(min, max))
  }

  if (filter?.unconfirmed !== undefined) {
    conditions.push(new UnConfirmedPromiseCondition(filter.unconfirmed))
  }

  if (filter?.confirmed !== undefined) {
    conditions.push(new ConfirmedPromiseCondition(filter.confirmed))
  }

  const lastName = filter?.last_name?.split(',')
  const start = lastName?.[0]
  const end = lastName?.[1]

  if (start || end) {
    conditions.push(new LastNameCondition(start, end))
  }

  const locations = filter?.locations
    ?.split(',')
    .map(location => ({ id: location }))

  if (locations?.length) {
    conditions.push(new LocationCondition(locations))
  }

  const accountManagers = filter?.account_managers
    ?.split(',')
    .map(manager => ({ pkid: parseInt(manager) }))

  if (accountManagers?.length) {
    conditions.push(new AccountManagerCondition(accountManagers))
  }

  const tags = filter?.account_tags?.split(',')
  if (tags) {
    conditions.push(new AccountTagsCondition(tags))
  }

  const statuses = filter?.account_status?.split(',').filter(Boolean)
  if (statuses?.length) {
    conditions.push(new AccountStatusCondition(statuses))
  }

  if (filter.autopay !== undefined) {
    conditions.push(new AutopayCondition(filter.autopay))
  }

  if (filter.scheduled_payment !== undefined) {
    conditions.push(new ScheduledPaymentCondition(filter.scheduled_payment))
  }

  if (filter.registered !== undefined) {
    if (filter.registered) {
      conditions.push(new RegisteredCustomerCondition())
    } else {
      conditions.push(new UnregisteredCustomerCondition())
    }
  }

  if (filter.text_enabled !== undefined) {
    conditions.push(new TextEnabledCondition(filter.text_enabled))
  }

  if (filter.email_enabled !== undefined) {
    conditions.push(new EmailEnabledCondition(filter.email_enabled))
  }

  if (filter.ai_calls_enabled !== undefined) {
    conditions.push(new AiCallsEnabledCondition(filter.ai_calls_enabled))
  }

  if (filter.action_types) {
    conditions.push(
      new CollectorActionCondition(
        filter.action_types,
        filter.action_user_id || filter.user_id
      )
    )
  }

  if (filter.action_date_from || filter.action_date_to) {
    conditions.push(
      new ActionDateRangeCondition(
        filter.action_date_from,
        filter.action_date_to
      )
    )
  }

  return conditions
}
